import { useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { infraKeys, productId } from '@/hooks/useInfra'
import { infraService } from '@/services/infraService'
import { useInfrastructureWizard } from '@/stores/infrastructureWizard'
import { serverRolesFor } from '@/utils/infraValidation'
import { SERVER_ROLE_LABELS, hasNamingRule, nameFor, sequenceKey, serverNameSeries } from '@/utils/naming'

/**
 * Asks again, just before the Infrastructure step is saved, for the next free number of every name series the flow generates: a server
 * may have been created with that number since it was shown. Returns one message per name that changed (the new number is then
 * in the store and in the cache, so the cards show it); an empty list means every name is still free. The names are a preview either way:
 * the pipeline reserves the real ones, and checks again, just before Terraform runs.
 */
export function useServerNameRecheck() {
  const queryClient = useQueryClient()
  return useCallback(async (): Promise<string[]> => {
    const data = useInfrastructureWizard.getState()
    if (!data.product || !hasNamingRule(data.product)) return []
    const environmentId = data.environment?.id ?? ''
    const changed: string[] = []
    for (const { role } of serverRolesFor(data)) {
      const series = serverNameSeries({ product: data.product, environmentId, role })
      if (!series.ok) continue
      const key = sequenceKey(environmentId, series.series)
      const before = data.serverSequences[key]
      const fresh = await infraService.getNextServerSequence(environmentId, data.product, role)
      queryClient.setQueryData(infraKeys.serverSequence(environmentId, productId(data.product), role), fresh)
      if (before && before.next === fresh.next) continue
      useInfrastructureWizard.getState().setServerSequence(key, fresh)
      changed.push(
        before
          ? `${SERVER_ROLE_LABELS[role]}: ${nameFor(series.series, before.next)} has been taken since it was shown, so the name is now ${nameFor(series.series, fresh.next)}.`
          : `${SERVER_ROLE_LABELS[role]}: the name is ${nameFor(series.series, fresh.next)}.`,
      )
    }
    return changed
  }, [queryClient])
}
